import type UAParser from 'ua-parser-js';

import { type VisitorService } from '@/shared/services/visitor';

export type KinesisEventProps = Record<
  string,
  string | number | string[] | number[]
>;

type UniqueSession = {
  id: string;
  started: number;
};

type CookiesStore = Pick<Cookies.CookiesStatic, 'get' | 'set'>;

/**
 * Sends analytics events to AWS Kinesis through a lambda URL. Every event carries general
 * props (visitor, session, device, page), the additional props set for the page and its own
 * props. Event ids, session number and session data live in cookies shared across our
 * subdomains.
 */
export default class KinesisService {
  private readonly COOKIE_LAST_EVENT_TIMESTAMP = 'ks_last_event_timestamp';
  private readonly COOKIE_EVENT_ID_NAME = 'ks_event_id';
  private readonly COOKIE_SESSION_NUMBER_NAME = 'ks_session_number';
  private readonly COOKIE_SESSION_DATA_NAME = 'ks_session_data';
  private readonly SESSION_DURATION = 10 * 60 * 1000; // 10min

  private additionalEventProps: KinesisEventProps = {};

  constructor(
    private readonly cookiesService: CookiesStore,
    private readonly visitorService: VisitorService,
    private readonly detector: UAParser.IResult,
    private readonly lambdaFunctionUrl: string | undefined,
  ) {}

  /** Starts or continues the session and reports the page visit. */
  init() {
    this.sendSessionStart();
    this.sendEvent('product_page_visited', { page_group: 'product' });
  }

  /** Props added to every following event (an event's own props win). */
  setAdditionalEventProps(props: KinesisEventProps) {
    this.additionalEventProps = { ...this.additionalEventProps, ...props };
  }

  sendEvent(eventName: string, eventProps: KinesisEventProps = {}) {
    setTimeout(() => {
      const generalProps = this.getEventGeneralProps();

      const recordData = {
        ...generalProps,
        ...this.additionalEventProps,
        ...eventProps,
        event_name: eventName,
      };

      this.setEventTimestamp();

      this.clientSend(recordData);
    }, 0);
  }

  private sendSessionStart(eventProps: KinesisEventProps = {}) {
    const DEFAULT_VALUE = '0';
    const lastEventCookie =
      this.cookiesService.get(this.COOKIE_LAST_EVENT_TIMESTAMP) ??
      DEFAULT_VALUE;

    const lastEventTimestamp = Number(lastEventCookie);

    const isNewSession =
      Number.isNaN(lastEventTimestamp) ||
      Date.now() - lastEventTimestamp > this.SESSION_DURATION;

    if (isNewSession) {
      this.setEventId(0);
      this.updateSessionData();
      this.incrementSessionNumber();
      this.sendEvent('session_start', eventProps);
    }

    if (lastEventCookie === DEFAULT_VALUE) {
      this.sendEvent('first_launch', eventProps);
    }
  }

  private clientSend(recordData: KinesisEventProps | KinesisEventProps[]) {
    if (localStorage?.getItem('debug_kinesis')) {
      if (Array.isArray(recordData)) {
        recordData.forEach((item) => {
          // eslint-disable-next-line no-console
          console.log(item.event_name, JSON.stringify(item));
        });
      } else {
        // eslint-disable-next-line no-console
        console.log(recordData.event_name, JSON.stringify(recordData));
      }
    }

    if (!this.lambdaFunctionUrl) {
      return;
    }

    fetch(this.lambdaFunctionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(recordData),
    }).catch(() => {});
  }

  private setCookie(name: string, value: string) {
    this.cookiesService.set(name, value, {
      expires: 365,
      domain: this.visitorService.cookieDomain,
    });
  }

  private setEventTimestamp() {
    this.setCookie(this.COOKIE_LAST_EVENT_TIMESTAMP, Date.now().toString());
  }

  private setEventId(id: number) {
    this.setCookie(this.COOKIE_EVENT_ID_NAME, id.toString());
  }

  private getEventId(): number {
    const cookieValue = Number(
      this.cookiesService.get(this.COOKIE_EVENT_ID_NAME) ?? '0',
    );
    const prevEventId = Number.isNaN(cookieValue) ? 0 : cookieValue;
    const nextEventId = prevEventId + 1;

    this.setEventId(nextEventId);

    return nextEventId;
  }

  private incrementSessionNumber() {
    const cookieValue = Number(
      this.cookiesService.get(this.COOKIE_SESSION_NUMBER_NAME) ?? '0',
    );
    const prevSessionNumber = Number.isNaN(cookieValue) ? 0 : cookieValue;

    this.setCookie(
      this.COOKIE_SESSION_NUMBER_NAME,
      (prevSessionNumber + 1).toString(),
    );
  }

  private getSessionNumber(): number {
    const cookieValue = Number(
      this.cookiesService.get(this.COOKIE_SESSION_NUMBER_NAME) ?? '0',
    );
    const sessionNumber = Number.isNaN(cookieValue) ? 0 : cookieValue;

    if (sessionNumber === 0) {
      this.incrementSessionNumber();

      return 1;
    }

    return sessionNumber;
  }

  private updateSessionData(): UniqueSession {
    const data: UniqueSession = {
      id: 'randomUUID' in crypto ? crypto.randomUUID() : '',
      started: Date.now(),
    };

    this.setCookie(this.COOKIE_SESSION_DATA_NAME, JSON.stringify(data));

    return data;
  }

  private getSessionData(): UniqueSession {
    try {
      const cookieValue =
        this.cookiesService.get(this.COOKIE_SESSION_DATA_NAME) ?? '{}';
      const cookieData = JSON.parse(cookieValue) as UniqueSession;

      if ('id' in cookieData && 'started' in cookieData) {
        return cookieData;
      }

      throw new Error('can`t parse COOKIE_SESSION_DATA_NAME');
    } catch (_error) {
      return this.updateSessionData();
    }
  }

  private getEventGeneralProps(): KinesisEventProps {
    const { detector } = this;

    const userId = this.cookiesService.get('ks_user_id'); // created by backend for logged users
    const timeZoneOffset =
      new Date().toString().match(/([-+][0-9]+)\s/)?.[1] ?? '';
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const sessionNumber = this.getSessionNumber();
    const isFirstSession = sessionNumber === 1 ? 1 : 0;
    const sessionData = this.getSessionData();

    const browserName =
      detector.browser.name && detector.browser.version
        ? `${detector.browser.name} ${detector.browser.version}`
        : 'unknown';

    const currentUrl = new URL(window.location.href);
    const deviceId = currentUrl.searchParams.get('udid') ?? '';

    const visitorCountry = (
      this.cookiesService.get('visitor_country') ?? ''
    ).trim();
    const gaCookie = (this.cookiesService.get('_ga_E1HB7EM8VF') ?? '').trim();

    return {
      user_id: userId ?? '',
      visitor_id: this.visitorService.visitorUUID,
      event_id: this.getEventId(),
      event_time_utc: Date.now(),
      time_offset: timeZoneOffset,
      time_zone: timeZone,
      session_number: sessionNumber,
      session_id: sessionData.id,
      session_started: sessionData.started,
      is_first_session: isFirstSession,
      platform: 'web',
      device_category: detector.device.type ?? 'desktop',
      os_name: detector.os.name ?? 'unknown',
      os_version: detector.os.version ?? 'unknown',
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      browser_group: detector.browser.name ?? 'unknown',
      browser_name: browserName,
      browser_user_agent: navigator.userAgent,
      app_language: document.documentElement.lang,
      event_source: 'web',
      initial_page_link: document.referrer,
      page_name: window.location.pathname,
      page_link: window.location.href,
      device_id: deviceId,
      cloudflare_country: visitorCountry,
      ga_visitor_id: gaCookie,
    };
  }
}
